import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useCiAdminAuth } from '../../contexts/CiAdminAuthContext';
import {
  CIInlineAlert,
} from '../../components/CustomerIdentity/CustomerIdentityShared';
import { useCITranslate } from '../../i18n/customerIdentity';
import {
  platformForgotPassword,
  platformResetPassword,
  platformSend2faOtp,
} from '../../services/customerIdentityApi';

const fallbackTarget = '/cia/sites';

const MODE_LOGIN = 'login';
const MODE_FORGOT_REQUEST = 'forgot-request';
const MODE_FORGOT_CONFIRM = 'forgot-confirm';

export default function CiAdminLogin() {
  const t = useCITranslate();
  const auth = useCiAdminAuth();
  const mainAuth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Redirect target priority: router state (from <Navigate state>) > `?from=`
  // query param (used by the response interceptor when it bounces an expired
  // session) > the sites landing page.
  const redirectTo =
    location.state?.from ||
    searchParams.get('from') ||
    fallbackTarget;

  const [mode, setMode] = useState(MODE_LOGIN);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [method, setMethod] = useState('totp');
  const [otpSent, setOtpSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setSubmitting(true);
    try {
      const result = await auth.login({ email: email.trim(), password });
      if (result?.requires2fa) {
        setChallenge(result.challenge);
        const methods = result.challenge?.method_options || ['totp'];
        setMethod(methods[0] || 'totp');
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      setMessage({ kind: 'danger', error: err });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendOtp = async () => {
    if (!challenge?.challenge_token || method === 'totp') return;
    setMessage(null);
    setSubmitting(true);
    try {
      await platformSend2faOtp({
        challenge_token: challenge.challenge_token,
        method,
      });
      setOtpSent(true);
    } catch (err) {
      setMessage({ kind: 'danger', error: err });
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!challenge?.challenge_token) return;
    setMessage(null);
    setSubmitting(true);
    try {
      await auth.verify2fa({
        challenge_token: challenge.challenge_token,
        method,
        code: code.trim(),
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setMessage({ kind: 'danger', error: err });
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setMessage(null);
    setSubmitting(true);
    try {
      const result = await platformForgotPassword({ email: email.trim() });
      // The backend responds the same way for unknown emails so admins can't
      // enumerate valid users. In dev mode it returns the token directly so
      // we can pre-fill the next step.
      if (result?.token_for_test) {
        setResetToken(result.token_for_test);
        setMessage({
          kind: 'info',
          text: 'Reset token issued (development mode). Enter it below with your new password.',
        });
      } else {
        setMessage({
          kind: 'success',
          text: 'If the email is registered, a reset link will be sent.',
        });
      }
      setMode(MODE_FORGOT_CONFIRM);
    } catch (err) {
      setMessage({ kind: 'danger', error: err });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    setMessage(null);
    setSubmitting(true);
    try {
      await platformResetPassword({
        token: resetToken.trim(),
        newPassword,
      });
      setMessage({
        kind: 'success',
        text: 'Password updated. You can now sign in with the new password.',
      });
      setPassword('');
      setNewPassword('');
      setResetToken('');
      setMode(MODE_LOGIN);
    } catch (err) {
      setMessage({ kind: 'danger', error: err });
    } finally {
      setSubmitting(false);
    }
  };

  const goToForgot = () => {
    setMessage(null);
    setMode(MODE_FORGOT_REQUEST);
  };

  const goBackToLogin = () => {
    setMessage(null);
    setMode(MODE_LOGIN);
    setChallenge(null);
    setCode('');
    setOtpSent(false);
  };

  const headingText = (() => {
    if (mode === MODE_FORGOT_REQUEST) return 'Forgot password';
    if (mode === MODE_FORGOT_CONFIRM) return 'Set a new password';
    return 'Customer Identity admin';
  })();

  const subText = (() => {
    if (mode === MODE_FORGOT_REQUEST) {
      return 'Enter the email associated with your platform account and we will send a reset link.';
    }
    if (mode === MODE_FORGOT_CONFIRM) {
      return 'Paste the reset token you received and choose a new password (min 8 characters).';
    }
    return 'Sign in with your platform user credentials. You will only see and manage the sites you have been granted access to.';
  })();

  return (
    <div className="ci-admin-login-shell ci-ui d-flex align-items-center justify-content-center">
      <div className="ci-admin-login-card">
        <div className="text-center mb-4">
          <h1 className="ci-admin-login-title">{headingText}</h1>
          <p className="ci-admin-login-sub">{subText}</p>
        </div>

        <CIInlineAlert
          message={message}
          onDismiss={() => setMessage(null)}
        />

        {mode === MODE_FORGOT_REQUEST ? (
          <form onSubmit={handleForgotRequest} noValidate>
            <div className="mb-3">
              <label className="form-label" htmlFor="cia-forgot-email">
                Email
              </label>
              <input
                id="cia-forgot-email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                disabled={submitting}
              />
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary flex-fill"
                onClick={goBackToLogin}
                disabled={submitting}
              >
                Back to sign in
              </button>
              <button
                type="submit"
                className="btn ci-btn-primary text-white flex-fill"
                disabled={submitting || !email.trim()}
              >
                {submitting ? t('common.loading') : 'Send reset link'}
              </button>
            </div>
          </form>
        ) : null}

        {mode === MODE_FORGOT_CONFIRM ? (
          <form onSubmit={handleResetConfirm} noValidate>
            <div className="mb-3">
              <label className="form-label" htmlFor="cia-reset-token">
                Reset token
              </label>
              <input
                id="cia-reset-token"
                type="text"
                className="form-control"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                required
                disabled={submitting}
                spellCheck={false}
                autoComplete="one-time-code"
              />
              <div className="form-text">
                Paste the token you received by email.
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="cia-new-password">
                New password
              </label>
              <input
                id="cia-new-password"
                type="password"
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                disabled={submitting}
              />
              <div className="form-text">At least 8 characters.</div>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary flex-fill"
                onClick={() => setMode(MODE_FORGOT_REQUEST)}
                disabled={submitting}
              >
                {t('common.back')}
              </button>
              <button
                type="submit"
                className="btn ci-btn-primary text-white flex-fill"
                disabled={submitting || !resetToken.trim() || newPassword.length < 8}
              >
                {submitting ? t('common.loading') : 'Update password'}
              </button>
            </div>
          </form>
        ) : null}

        {mode === MODE_LOGIN && !challenge ? (
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label className="form-label" htmlFor="cia-email">
                Email
              </label>
              <input
                id="cia-email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                disabled={submitting}
              />
            </div>
            <div className="mb-3 d-flex justify-content-between align-items-baseline">
              <label className="form-label mb-0" htmlFor="cia-password">
                Password
              </label>
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={goToForgot}
                disabled={submitting}
              >
                Forgot password?
              </button>
            </div>
            <div className="mb-3">
              <input
                id="cia-password"
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={submitting}
              />
            </div>
            <button
              type="submit"
              className="btn ci-btn-primary text-white w-100"
              disabled={submitting}
            >
              {submitting ? t('common.loading') : 'Sign in'}
            </button>
          </form>
        ) : null}

        {mode === MODE_LOGIN && challenge ? (
          <form onSubmit={handleVerify} noValidate>
            <p className="text-muted small">
              Two-factor verification is required for this account.
            </p>
            <div className="mb-3">
              <label className="form-label">Method</label>
              <div className="btn-group w-100" role="group">
                {(challenge.method_options || ['totp']).map((m) => (
                  <button
                    type="button"
                    key={m}
                    className={`btn ${method === m ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => {
                      setMethod(m);
                      setOtpSent(false);
                    }}
                  >
                    {m === 'totp' ? 'Authenticator' : m === 'email' ? 'Email' : 'SMS'}
                  </button>
                ))}
              </div>
            </div>
            {method !== 'totp' ? (
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={handleSendOtp}
                  disabled={submitting}
                >
                  {otpSent ? 'Resend code' : 'Send code'}
                </button>
              </div>
            ) : null}
            <div className="mb-3">
              <label className="form-label" htmlFor="cia-code">
                Verification code
              </label>
              <input
                id="cia-code"
                type="text"
                inputMode="numeric"
                className="form-control text-center"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={8}
                required
                disabled={submitting}
                style={{ letterSpacing: '0.4em', fontFamily: 'monospace' }}
              />
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary flex-fill"
                onClick={() => {
                  setChallenge(null);
                  setCode('');
                  setOtpSent(false);
                }}
                disabled={submitting}
              >
                {t('common.back')}
              </button>
              <button
                type="submit"
                className="btn ci-btn-primary text-white flex-fill"
                disabled={submitting || !code}
              >
                {submitting ? t('common.loading') : 'Verify'}
              </button>
            </div>
          </form>
        ) : null}

        {mainAuth?.isAuthenticated ? (
          <div className="ci-admin-login-alt mt-4 pt-3 border-top text-center small text-muted">
            Already signed in to the main app?{' '}
            <Link to="/customer-identity" className="ci-admin-login-alt-link">
              Open Customer Identity here
            </Link>
            {' '}without a separate login.
          </div>
        ) : null}
      </div>
    </div>
  );
}
